/**
 * <sunday-m-hero>: the mattress buy hero and its sticky buy bar (sections/sunday-m-hero.liquid), mockup v111.
 *
 * Size: thickness (when the variants have two), the main width tiles (name, length × width, price), "Other sizes ›"
 * (the other widths, Custom, the length switch), and a custom size cut from the next standard size up. The Add
 * button, the EMI line, the three mattress tiles (each at the chosen size), the bar and the closing band follow
 * the choice. The chosen size is remembered site-wide (@sunday/state "bed") and written to ?variant=; a size
 * chosen in Help me choose on this page is followed at once.
 * The bar (phone and tablet) shows once the in-page Add has scrolled away above, until the talk card; its size
 * chip opens the size sheet ("sunday:bar" tells the "Talk to us" pill, assets/sunday-tpill.js, to step aside).
 * Add answers the tap at once (sunday:adding) and the cart opens when it answers (sunday:added). Once this size
 * is in the cart the Adds read "In your cart · Go to cart" and "Complete your bed" shows its three rows.
 * From Help me choose's "See this pair" (mattress + topper) the topper row above Add is ticked: the Adds read
 * "Add both · ₹mattress+topper" and one tap adds both to the same bed at the size; unticked, they read Add again.
 * Gallery thumbs (and a swipe on the stage) swap the stage between the photos, the render on the mattress
 * tint, the turning video, the clips and the full film (with sound and controls).
 * Every word comes from the section (cfg.text) or the store; no product is named here.
 */
import { parseAll, uniq, cutFrom, tileName, widthName, CUT } from '@sunday/sizes';
import * as state from '@sunday/state';
import * as cart from '@sunday/cart';
import { paint as paintIn } from '@sunday/incart';
import { esc, money, emi, calm } from '@sunday/util';
import { cameForPair } from '@sunday/choose';

const CM = 2.54;
const cm = (n) => Math.round(n * CM);
const fill = (s, v) => String(s || '').replace(/\[(\w+)\]/g, (m, k) => (k in v ? v[k] : m));
const $ = (s, r = document) => r.querySelector(s);
const IB = '<svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true"><circle cx="8" cy="8" r="6.6" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M8 7.2v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><circle cx="8" cy="4.9" r=".95" fill="currentColor"/></svg>';
const $$ = (s, r = document) => [...r.querySelectorAll(s)];

class SundayMHero extends HTMLElement {
  connectedCallback() {
    if (this.cfg) return;
    this.cfg = JSON.parse($('script[data-cfg]', this).textContent);
    const c = this.cfg;
    this.t = c.text;
    this.sizes = parseAll(c.variants);
    this.heights = uniq(this.sizes.map((s) => s.h).filter((h) => h != null));
    this.others = Object.fromEntries(Object.entries(c.others || {}).map(([k, v]) => [k, parseAll(v)]));
    this.unit = 'in';
    this.custom = null;
    this.measure = null;

    // 21 Oct (edge cases): never start on a sold-out size; the configured default only when it is in stock.
    const live = this.sizes.filter((s) => s.available);
    const start = this.sizes.find((s) => s.id === c.selectedId && s.available) || live[0] || this.sizes[0];
    this.pick = { h: start?.h ?? null, w: start?.w, l: start?.l };
    const bed = state.get('bed');
    const dm = /^(\d+)\s*x\s*(\d+)$/i.exec(c.defaultSize || '');
    if (!c.urlHasVariant && !bed && dm) {
      const d = this.sizes.find((s) => s.w === +dm[1] && s.l === +dm[2] && s.available);
      if (d) this.pick = { h: d.h, w: d.w, l: d.l };
    }
    if (!c.urlHasVariant && bed && bed.w && bed.l) {
      const std = this.#std(bed.w, bed.l);
      if (bed.custom && std) this.custom = { w: bed.w, l: bed.l };
      else if (std) this.pick = { h: std.h, w: std.w, l: std.l };
    }
    try {
      if (c.units && localStorage.getItem('sunday-unit') === 'cm') this.unit = 'cm';
    } catch (e) {}
    this.mains = (c.mainWidths || []).map(Number);
    const most = Number(c.most) || 78;
    this.more = !this.custom && (!this.mains.includes(this.pick.w) || this.pick.l !== most);

    this.$sizes = $('[data-sizes]', this);
    this.$sheetSizes = $('[data-sizes-sheet]', this);
    this.$price = $('#buy [data-price]', this);
    this.$emil = $('[data-emil]', this);
    /* 24 Oct: the line under Add wraps by parts; a part that starts a new line drops its leading dot */
    const under = $('.sd-mh-under', this);
    if (under && 'ResizeObserver' in window) {
      const parts = [...under.children];
      const fix = () => {
        const dots = parts.map((p) => $('.sd-mh-dot', p));
        dots.forEach((d) => d && (d.hidden = false));
        const tops = parts.map((p) => p.offsetTop);
        dots.forEach((d, i) => d && i && (d.hidden = tops[i] > tops[i - 1] + 2));
      };
      new ResizeObserver(fix).observe(under);
      fix();
    }
    this.$what = $('[data-what]', this);
    this.$add = $('#buy [data-add]', this);
    this.$err = $('[data-err]', this);
    this.$emi = $('[data-emi]', this);
    this.$emiBtn = $('[data-emi-tip]', this);
    this.emiBase = this.$emiBtn ? this.$emiBtn.dataset.tip : '';
    this.$measure = $('[data-measure]', this);
    this.$bar = $('[data-bar]', this);
    this.$sheet = $('[data-size-sheet], [data-sheet]', this);
    this.$chip = $('[data-chip]', this);
    this.#pairSetup();

    this.addEventListener('click', (e) => this.#click(e));
    this.addEventListener('input', (e) => this.#input(e));
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && e.target.matches('[data-mw],[data-ml]')) this.#useCustom();
    });
    this.#bubbles();
    this.#gallery();
    this.#bedSetup();
    this.#barWatch();
    this.render();
    document.addEventListener('sunday:state', (e) => e.detail && e.detail.key === 'bed' && this.#follow(e.detail.value));
  }

  /* ---------- sizes ---------- */
  #at() {
    return this.sizes.filter((s) => this.pick.h == null || s.h === this.pick.h);
  }
  #std(w, l) {
    const live = this.sizes.filter((s) => s.available);
    const exact = live.find((s) => s.w === w && s.l === l && (this.pick.h == null || s.h === this.pick.h));
    return exact || cutFrom(live, w, l, this.pick.h);
  }
  get chosen() {
    // 21 Oct (edge cases): a sold-out size is never chosen (Add stays off); a custom size is cut from one in stock.
    if (this.custom) return cutFrom(this.sizes.filter((s) => s.available), this.custom.w, this.custom.l, this.pick.h);
    return this.#at().find((s) => s.w === this.pick.w && s.l === this.pick.l && s.available) || null;
  }
  #in(n) {
    return this.unit === 'cm' ? `${cm(n)} cm` : `${n}″`;
  }
  /** "Queen size 78×60″" (or 198 × 152 cm); for the 2-height mattress, " · 6″" after it. */
  #label(s, short = false) {
    if (!s) return '';
    let name = widthName(s);
    if (short) name = name.replace(/\s+(size|bed)$/i, '');
    const dims = this.unit === 'cm' ? `${cm(s.l)} × ${cm(s.w)} cm` : `${s.l}×${s.w}″`;
    const h = this.heights.length > 1 && s.h ? ` · ${this.#in(s.h)}` : '';
    return `${name} ${dims}${h}`;
  }
  #what() {
    const v = this.chosen;
    if (!v) return '';
    if (this.custom) {
      const dims = this.unit === 'cm' ? `${cm(this.custom.l)} × ${cm(this.custom.w)} cm` : `${this.custom.l}×${this.custom.w}″`;
      return fill(this.t.customLine, { size: dims, from: this.#label(v) });
    }
    return this.#label(v);
  }

  /* The page prints the same picker at first paint (snippets/sunday-m-sizes.liquid): keep the two in step.
     Thickness (two heights), the main width tiles, "Other sizes ›" (the other widths, Custom, length, note). */
  #sizesHtml(sheet = false) {
    const t = this.t;
    const at = this.#at();
    const most = Number(this.cfg.most) || 78;
    const custOn = !!(this.custom || this.measure);
    const out = [];
    const find = (list, w, l, h) => list.find((x) => x.w === w && x.l === l && (h == null || x.h === h));
    if (this.heights.length > 1) {
      const base = this.heights[0];
      const btn = (h) => {
        let plus = '';
        if (h !== base) {
          const ws = uniq(this.sizes.filter((x) => x.h === h).map((x) => x.w));
          const w = ws.includes(this.pick.w) ? this.pick.w : ws[0];
          const l = find(this.sizes, w, this.pick.l, h) && find(this.sizes, w, this.pick.l, base) ? this.pick.l : most;
          const hi = find(this.sizes, w, l, h);
          const lo = find(this.sizes, w, l, base);
          if (hi && lo && hi.price > lo.price) plus = ' · ' + fill(t.plus, { amount: money(hi.price - lo.price) });
        }
        const word = esc(fill(t.thick, { n: h })) + esc(plus);
        return `<button type="button" data-h="${h}" aria-pressed="${this.pick.h === h}">${word}</button>`;
      };
      const btns = this.heights.map(btn).join('');
      out.push(`<div class="sd-mh-thk" role="group" aria-label="${esc(t.thickness)}">${btns}</div>`);
    }
    const allW = uniq(this.sizes.map((x) => x.w));
    /* 21 Oct (AR): as live: a tile is a width ("60″ wide"), the length is its own row, always shown; the tile adds
       its price at the chosen length. The size sheet (sticky bar) keeps length × width. */
    /* 24 Oct (AR go): on the page a tile reads "Queen / 60×78″ / ₹25,999" (width × length at the chosen length) */
    const wideTxt = (w, l) => (sheet ? `${this.pick.l}×${w}″` : this.unit === 'cm' ? `${cm(w)}×${cm(l)} cm` : `${w}×${l}″`);
    const tile = (w) => {
      const ls = at.filter((x) => x.w === w);
      const s = ls.find((x) => x.l === this.pick.l) || ls.find((x) => x.l === most) || ls[ls.length - 1];
      const on = !custOn && this.pick.w === w;
      const any = s || this.sizes.find((x) => x.w === w);
      // 21 Oct (edge cases): sold out at this length: the tile stays, says so, and can't be picked.
      if (s && !s.available) return `<button type="button" class="sd-mh-szt sd-mh-szt-no" data-w="${w}" aria-pressed="false" aria-disabled="true" disabled><b>${esc(tileName(any))}</b><small>${s.l}×${w}″</small><em>${esc(t.soldOut || '')}</em></button>`;
      if (s) return `<button type="button" class="sd-mh-szt" data-w="${w}" aria-pressed="${on}"><b>${esc(tileName(any))}</b><small>${wideTxt(w, s.l)}</small><em>${money(s.price)}</em></button>`;
      /* Not made at this thickness: keep the size, and say which thickness has it. */
      const hs = uniq(this.sizes.filter((x) => x.w === w && x.h != null).map((x) => x.h));
      const only = hs.length && t.onlyH ? fill(t.onlyH, { n: hs.join(' / ') }) : '';
      return `<button type="button" class="sd-mh-szt sd-mh-szt-no" data-w="${w}" aria-pressed="false" disabled><b>${esc(tileName(any))}</b><small>${any.l}×${w}″</small><em>${esc(only)}</em></button>`;
    };
    /* 24 Oct (AR go): the four main sizes, "Other sizes ›" beside "Your size" opens the rest (widths, Custom, length) */
    const flat = false;
    const mains = flat ? allW : allW.filter((w) => this.mains.includes(w));
    const extra = flat ? [] : allW.filter((w) => !this.mains.includes(w));
    const open = sheet || flat || this.more;
    /* 21 Oct (AR): open, "Fewer sizes" goes after the extra sizes, so no gap splits the size tiles.
       24 Oct (AR go): closed, it is a full-width tile under the four sizes that names what it holds:
       "More sizes · XL Queen, other lengths, custom ›" (the other widths come from the product's own sizes). */
    const moreList = [...extra.map((w) => tileName(this.sizes.find((x) => x.w === w))), t.otherLens, t.customLow].filter(Boolean).join(', ');
    const toggle = sheet || flat
      ? ''
      : open
        ? `<div class="sd-mh-szo"><button type="button" class="sd-mh-more" data-more aria-expanded="true">${esc(t.fewer)}</button></div>`
        : `<button type="button" class="sd-mh-szt sd-mh-moret" data-more aria-expanded="false"><b>${esc(t.moreTile)}</b><small> · ${esc(moreList)}</small><span aria-hidden="true">›</span></button>`;
    const cust = sheet
      ? `<button type="button" class="sd-mh-szt" data-custom-sheet aria-pressed="${custOn}"><b>${esc(t.custom)}</b><small>${esc(t.anySize)}</small><em aria-hidden="true">✂</em></button>`
      : `<button type="button" class="sd-mh-szt" data-custom aria-pressed="${custOn}"><b>${esc(t.custom)}</b><small>${this.custom ? esc(`${this.custom.l}×${this.custom.w}″`) : esc(t.anySize)}</small><em aria-hidden="true">✂</em></button>`;
    const lens = uniq(at.map((x) => x.l));
    const wNow = this.custom ? this.chosen?.w : this.pick.w;
    const lenBtn = (l) => {
      const ok = !!at.find((x) => x.w === wNow && x.l === l && x.available);
      const tag = l === most && t.most ? `<span class="sd-mh-most" aria-hidden="true">${esc(t.most)}</span>` : '';
      return `<button type="button" data-l="${l}" aria-pressed="${!custOn && this.pick.l === l}"${ok ? '' : ' disabled'}>${tag}${l}″</button>`;
    };
    /* 23 Oct (AR go): the price note moves into an ⓘ beside "Length" (a bubble, not a line).
       24 Oct (AR go): "8 in 10 beds are 78″ long. 72″, 75″ and 78″ cost the same." in the unit chosen (cm too),
       and one line under the lengths: "Not sure? Confirm it before we ship." */
    const mostP = at.find((x) => x.w === wNow && x.l === most);
    const sameLs = mostP ? lens.filter((l) => { const x = at.find((y) => y.w === wNow && y.l === l); return x && x.price === mostP.price; }) : [];
    const isCm = this.unit === 'cm';
    const lu = (l) => (isCm ? `${cm(l)}` : `${l}″`);
    const lastU = (s) => (isCm ? `${s} cm` : s);
    const list = sameLs.length > 1 ? lastU(sameLs.slice(0, -1).map(lu).join(', ') + (t.and || ' and ') + lu(sameLs[sameLs.length - 1])) : '';
    const tipTxt = t.lenTip && list ? fill(t.lenTip, { most: lastU(lu(most)), list }) : t.same;
    const note = !sheet && t.confirm && lens.length > 1 ? `<p class="sd-mh-lnote">${esc(t.confirm)}</p>` : '';
    const lenTip = tipTxt ? `<button type="button" class="sd-mh-i" data-tip="${esc(tipTxt)}" aria-label="${esc(tipTxt)}" aria-expanded="false">${IB}</button>` : '';
    const segBtns = lens.map(lenBtn).join('');
    const seg = lens.length > 1 ? `<div class="sd-mh-szl"><span>${esc(t.length)}${sheet ? '' : lenTip}</span><div class="sd-mh-seg" role="group" aria-label="${esc(t.length)}">${segBtns}</div></div>${note}` : '';
    if (flat) out.push(`<div class="sd-mh-szw sd-mh-szw-all" role="group" aria-label="${esc(t.width)}">${mains.map(tile).join('')}${cust}</div>${seg}`);
    else {
      out.push(`<div class="sd-mh-szw" role="group" aria-label="${esc(t.width)}">${mains.map(tile).join('')}</div>`);
      if (!open) out.push(toggle);
      out.push(`<div class="sd-mh-szx"${open ? '' : ' hidden'}><div class="sd-mh-szw sd-mh-szw-x">${extra.map(tile).join('')}${cust}</div>${seg}</div>`);
      if (open) out.push(toggle);
    }
    return out.join('');
  }

  /* The custom size box: your frame, drawn to scale, and the standard size we cut it from. */
  #measured() {
    const m = this.measure || {};
    const w = Number(m.w);
    const l = Number(m.l);
    const ok = w >= CUT.minW && w <= CUT.maxW && l >= CUT.minL && l <= CUT.maxL;
    return { w, l, ok, from: ok ? cutFrom(this.sizes, w, l, this.pick.h) : null };
  }
  #measureHtml() {
    const t = this.t;
    const m = this.measure;
    const isCm = this.unit === 'cm';
    const toU = (v) => (v === '' || v == null ? '' : isCm ? cm(v) : v);
    const { w, l, ok, from } = this.#measured();
    const sc = 2.2;
    const fw = ok ? w : 60;
    const fl = ok ? l : 78;
    const sw = from ? from.w : fw;
    const sl = from ? from.l : fl;
    const W = 200;
    const ox = (W - sw * sc) / 2 + 10;
    const oy = 10;
    const svg = `<svg class="sd-mh-cutsvg" viewBox="0 0 ${W + 20} ${sl * sc + 50}" role="img" aria-label="${esc(t.svgAria)}">
 <defs><pattern id="sd-mh-hat" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><path d="M0 0v6" stroke="var(--sd-line)" stroke-width="2"/></pattern></defs>
 <rect x="${ox}" y="${oy}" width="${sw * sc}" height="${sl * sc}" fill="none" stroke="var(--sd-ink3)" stroke-dasharray="5 4"/>
 ${from && (sw > fw || sl > fl) ? `<path d="M${ox + fw * sc} ${oy}h${(sw - fw) * sc}v${sl * sc}h-${(sw - fw) * sc}zM${ox} ${oy + fl * sc}h${fw * sc}v${(sl - fl) * sc}h-${fw * sc}z" fill="url(#sd-mh-hat)" opacity=".8"/>` : ''}
 <rect x="${ox - 6}" y="${oy - 6}" width="${fw * sc + 12}" height="${fl * sc + 12}" rx="4" fill="none" stroke="var(--sd-art-wood-dark)" stroke-width="5"/>
 <rect x="${ox}" y="${oy}" width="${fw * sc}" height="${fl * sc}" rx="3" fill="var(--sd-art-latex)" stroke="var(--sd-c)" stroke-width="3"/>
 <text x="${ox + (fw * sc) / 2}" y="${oy + (fl * sc) / 2}" text-anchor="middle" font-size="13" fill="var(--sd-ink)">${ok ? toU(w) + '×' + toU(l) : '—'}</text>
 <text x="${ox + (fw * sc) / 2}" y="${oy + (fl * sc) / 2 + 16}" text-anchor="middle" font-size="10" fill="var(--sd-ink2)">${esc(ok ? t.yourMattress : t.typeSize)}</text>
 <text x="${ox}" y="${oy + sl * sc + 30}" font-size="10.5" fill="var(--sd-ink2)">${esc(t.dashed)}</text></svg>`;
    const lim = isCm ? `${cm(CUT.maxL)}×${cm(CUT.maxW)} cm` : `${CUT.maxL}×${CUT.maxW}″`;
    const res = from
      ? `<div class="sd-mh-cutres"><b>${esc(fill(t.cutFrom, { from: this.#label(from) }))}</b><span>${esc(t.cutSub)}</span></div>`
      : (m.w || m.l) && !ok
        ? `<p class="sd-warn">${esc(fill(t.tooBig, { max: lim }))}</p>`
        : '';
    const sizeTxt = ok ? `${toU(w)}×${toU(l)}${isCm ? ' cm' : '″'}` : '';
    return `<div class="sd-mh-cut">
 <b class="sd-mh-cuth">${esc(t.insideSize)}</b>
 <div class="sd-mh-cutg"><div data-cutsvg>${svg}</div><div class="sd-mh-cutf">
 <div class="sd-mh-cutin"><label>${esc(t.width)}<input data-mw inputmode="numeric" value="${esc(toU(m.w))}" placeholder="${esc(fill(t.eg, { n: isCm ? 147 : 58 }))}"></label><label>${esc(t.length)}<input data-ml inputmode="numeric" value="${esc(toU(m.l))}" placeholder="${esc(fill(t.eg, { n: isCm ? 193 : 76 }))}"></label></div>
 <p class="sd-mh-cutm">${esc(fill(t.measureLine, { max: lim }))}</p>
 <div data-cutres aria-live="polite">${res}</div>
 <button type="button" class="sd-btn sd-nv sd-full sd-mh-use" data-use${from ? '' : ' disabled'}>${esc(ok ? fill(t.useSize, { size: sizeTxt }) : t.useThis)}</button></div></div></div>`;
  }

  /* ---------- paint ---------- */
  render() {
    const v = this.chosen;
    const t = this.t;
    this.$sizes.innerHTML = this.#sizesHtml(false);
    const sh = $('#buy .sd-mh-sh', this);
    if (sh) {
      $$('.sd-mh-szo', sh).forEach((n) => n.remove());
      sh.parentElement.classList.toggle('sd-open', !!this.more);
    }
    if (this.$sheetSizes) this.$sheetSizes.innerHTML = this.#sizesHtml(true);
    $$('[data-unit]', this).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.unit === this.unit)));
    this.$measure.hidden = !this.measure;
    this.$measure.innerHTML = this.measure ? this.#measureHtml() : '';
    /* 24 Oct (AR go): "8″ thick" chip on the photo follows the chosen size (Ortho Memory 4: 6″ or 8″) */
    const tc = $('[data-thick-chip] b', this);
    if (tc && v && v.h) tc.textContent = `${v.h}″`;
    const price = v ? money(v.price) : '';
    if (this.$price) this.$price.textContent = price;
    if (this.$what) this.$what.textContent = this.#what();
    this.$add.disabled = !v;
    const tp = this.#pairNow();
    const both = tp && this.pair.on;
    // 24 Oct (AR go): no price line above Add; Add carries it ("Add to cart · ₹25,999")
    this.$add.textContent = v ? (both ? `${t.addBoth} · ${money(v.price + tp.price)}` : `${t.add} · ${money(v.price)}`) : t.add;
    if (this.$emil && v && this.cfg.emiMonths) this.$emil.textContent = fill(t.emiLine, { amount: money(emi(v.price, this.cfg.emiMonths)) });
    const ba = $('[data-bar-add]', this);
    if (ba) ba.textContent = both ? t.addBoth : t.add;
    if (this.$emi && v && this.cfg.emiMonths) this.$emi.textContent = fill(t.perkEmi, { amount: money(emi(v.price, this.cfg.emiMonths)) });
    if (this.$emiBtn && v && this.cfg.emiMonths && t.emiNow) {
      const months = this.cfg.emiMonths;
      this.$emiBtn.dataset.tip = `${this.emiBase} ${fill(t.emiNow, { size: this.#label(v, true), a3: money(emi(v.price, 3)), a6: money(emi(v.price, months)), months })}`.trim();
    }
    const bp = $('[data-bar-price]', this);
    if (bp) bp.textContent = both ? money(v.price + tp.price) : price;
    // In your cart · Go to cart once this size is in the cart (@sunday/incart).
    $$('[data-add]', this).forEach((b) => this.#inCart(b, v));
    const cl = $('[data-chip-label]', this);
    if (cl) cl.textContent = this.custom ? this.#what().split(',')[0] : this.#label(v, true);
    /* 22 Oct (AR): the three mattress tiles keep their From price (no size) */
    this.#bedPrices();
    this.#pairPaint(tp);
    this.#closer();
    this.#emit();
  }

  #inCart(b, v) {
    b.dataset.sdKind = 'm';
    b.dataset.sdV = v ? v.id : '';
    b.dataset.sdCustom = v && this.custom ? cart.customSizeText(this.custom.w, this.custom.l) : '';
    paintIn(b);
  }

  /* The closing band (desktop) at the foot of the page: name, size, price, and the same in-cart state. */
  #closer() {
    const v = this.chosen;
    $$('[data-closer]').forEach((c) => {
      const sz = $('[data-closer-size]', c);
      if (sz) sz.textContent = fill(this.t.closer, { size: this.#what() });
      const pr = $('[data-closer-price]', c);
      if (pr) pr.textContent = v ? money(v.price) : '';
      const b = $('[data-closer-btn]', c);
      if (b) this.#inCart(b, v);
    });
  }

  /* Each mattress tile shows its price at the chosen size (its nearest standard size). */
  #tilePrices() {
    const v = this.chosen;
    if (!v) return;
    const w = this.custom ? this.custom.w : v.w;
    const l = this.custom ? this.custom.l : v.l;
    $$('[data-tile-price]', this).forEach((el) => {
      const list = this.others[el.dataset.tilePrice] || [];
      const hs = uniq(list.map((s) => s.h).filter((h) => h != null));
      // This mattress's own tile: the height chosen here. The others: their lowest price at this size
      // (Ortho Memory 4 at 6″, as every other page shows it; AR, 17 Oct).
      const live = list.filter((x) => x.available);
      const pick = (h) => live.find((x) => x.w === w && x.l === l && (h == null || x.h === h)) || cutFrom(live, w, l, h);
      const own = el.closest('[aria-current="page"]');
      let s;
      if (own && hs.includes(v.h)) s = pick(v.h);
      else if (!hs.length) s = pick(null);
      else s = hs.map(pick).filter(Boolean).sort((a, b) => a.price - b.price)[0];
      if (s) el.textContent = money(s.price);
      const sz = el.parentElement && $('[data-tile-size]', el.parentElement);
      if (sz && s) sz.textContent = tileName(s);
    });
  }

  /* Complete your bed: each row priced for the chosen size (accessories come in standard widths from 36″,
     84″ long where the accessory has it, else 78″); shown once this mattress size is in the cart. */
  #bedSetup() {
    this.$cbed = $('[data-cbed]', this);
    if (!this.$cbed) return;
    this.accs = $$('[data-acc]', this.$cbed).map((el) => ({ el, key: el.dataset.acc, pid: Number(el.dataset.pid), sizes: parseAll(JSON.parse($('script', el).textContent)) }));
    const show = () => (this.$cbed.hidden = !this.$add.hasAttribute('data-sd-in'));
    new MutationObserver(show).observe(this.$add, { attributes: true, attributeFilter: ['data-sd-in'] });
    show();
  }
  #accSize(a) {
    const v = this.chosen;
    if (!v) return null;
    const ok = a.sizes.filter((x) => x.available);
    if (a.key === 'pillow') return ok[0] || null;
    const w = Math.max(v.w, Math.min(...ok.map((x) => x.w)));
    return ok.find((x) => x.w === w && x.l === v.l) || ok.find((x) => x.w === w && x.l === 78) || cutFrom(ok, w, Math.min(v.l, 78)) || null;
  }
  #bedPrices() {
    (this.accs || []).forEach((a) => {
      const s = this.#accSize(a);
      a.el.hidden = !s;
      if (!s) return;
      const line = $('[data-acc-line]', a.el);
      const name = a.key === 'pillow' ? `${s.w}×${s.l}″` : `${tileName(s)} ${s.l}×${s.w}″`;
      line.textContent = `${name} · ${money(s.price)}`;
      const b = $('[data-acc-add]', a.el);
      b.dataset.sdV = s.id;
      paintIn(b);
    });
  }
  /* "See this pair": the topper row above Add (ticked on arrival, hidden once this size is in the cart). */
  #pairSetup() {
    const tp = this.cfg.topper;
    this.$pair = $('[data-pair]', this);
    this.pair = { show: false, on: false, acc: tp && this.$pair ? { key: 'topper', pid: tp.productId, sizes: parseAll(tp.variants) } : null };
    if (!this.pair.acc) return;
    this.pair.show = this.pair.on = cameForPair();
    document.addEventListener('sunday:pair', () => {
      this.pair.show = this.pair.on = true;
      this.render();
    });
    $('[data-pair-tick]', this.$pair).addEventListener('change', (e) => {
      this.pair.on = e.target.checked;
      this.render();
    });
    // In your cart (or back out of it): the row, the Adds and the bar price follow.
    let was = this.$add.hasAttribute('data-sd-in');
    new MutationObserver(() => {
      const now = this.$add.hasAttribute('data-sd-in');
      if (now === was) return;
      was = now;
      if (this.pair.show) this.render();
    }).observe(this.$add, { attributes: true, attributeFilter: ['data-sd-in'] });
  }
  /** The topper at the chosen size while the row is up, else null. */
  #pairNow() {
    return this.pair && this.pair.show && !this.$add.hasAttribute('data-sd-in') ? this.#accSize(this.pair.acc) : null;
  }
  #pairPaint(s) {
    if (!this.$pair) return;
    this.$pair.hidden = !s;
    if (!s) return;
    $('[data-pair-tick]', this.$pair).checked = this.pair.on;
    $('[data-pair-size]', this.$pair).textContent = `${tileName(s)} ${s.l}×${s.w}″`;
    $('[data-pair-price]', this.$pair).textContent = fill(this.t.plus, { amount: money(s.price) });
  }
  async #accAdd(btn) {
    const a = (this.accs || []).find((x) => x.el.contains(btn));
    const s = a && this.#accSize(a);
    if (!s || btn.dataset.busy) return;
    btn.dataset.busy = '1';
    try {
      const v = this.chosen;
      await cart.addFromProductPage({ variantId: s.id, productId: a.pid, size: a.key === 'pillow' ? null : { w: s.w, l: s.l }, anyBed: a.key === 'pillow' });
      document.dispatchEvent(new CustomEvent('sunday:added', { detail: { btn, stay: true, name: v ? this.cfg.name : '' } }));
    } catch (err) {
      document.dispatchEvent(new CustomEvent('sunday:add-failed', { detail: { btn, message: err.message || '' } }));
    } finally {
      delete btn.dataset.busy;
    }
  }

  #emit() {
    const v = this.chosen;
    const detail = { ready: !!v, price: v ? money(v.price) : '', label: this.#what(), custom: v && this.custom ? { ...this.custom } : null, w: v ? v.w : null, h: v ? v.h ?? null : null, wname: v ? tileName(v) : '' };
    window.sundayBuyLast = detail;
    this.dispatchEvent(new CustomEvent('sunday:buy', { bubbles: true, detail }));
  }

  #remember() {
    const v = this.chosen;
    if (!v) return;
    state.set('bed', this.custom ? { w: this.custom.w, l: this.custom.l, custom: true, from: this.cfg.name } : { w: v.w, l: v.l, from: this.cfg.name });
    if (!this.custom) this.#url(v.id);
  }
  #url(id) {
    // The address bar changes after the tap has painted: the Google tag listens to it and would delay the paint.
    requestAnimationFrame(() => setTimeout(() => {
      const url = new URL(location.href);
      url.searchParams.set('variant', id);
      history.replaceState(history.state, '', url);
    }));
  }

  /* A size chosen elsewhere on this page (Help me choose): the sizes, prices, tiles, bar and Add follow at once. */
  #follow(bed) {
    if (!bed || !bed.w || !bed.l || bed.from === this.cfg.name) return;
    const std = this.#std(Number(bed.w), Number(bed.l));
    if (!std) return;
    this.measure = null;
    if (bed.custom) this.custom = { w: Number(bed.w), l: Number(bed.l) };
    else {
      this.custom = null;
      this.pick = { h: std.h, w: std.w, l: std.l };
    }
    const most = Number(this.cfg.most) || 78;
    this.more = !this.custom && (!this.mains.includes(this.pick.w) || this.pick.l !== most);
    this.render();
    const v = this.chosen;
    if (v && !this.custom) this.#url(v.id);
  }

  /* ---------- actions ---------- */
  #click(e) {
    const b = e.target.closest('button, a');
    if (!b || !this.contains(b)) return;
    const d = b.dataset;
    if ('accAdd' in d) {
      this.#accAdd(b);
      return;
    }
    if ('more' in d) {
      this.more = !this.more;
      this.render();
      /* 21 Oct (AR): keep the focus on the button for keyboard users only (e.detail is 0 for Enter/Space); a tap
         left a square focus box around it */
      if (e.detail === 0) $('#buy [data-more]', this)?.focus();
      return;
    }
    if (d.h) {
      this.pick.h = Number(d.h);
      const at = this.#at();
      if (!at.some((s) => s.w === this.pick.w && s.l === this.pick.l)) {
        const near = at.find((s) => s.w === this.pick.w && s.l === Number(this.cfg.most)) || at.find((s) => s.w === this.pick.w) || at.find((s) => s.l === this.pick.l) || at[0];
        this.pick.w = near.w;
        this.pick.l = near.l;
      }
      if (this.custom && !this.chosen) this.custom = null;
      this.#remember();
    } else if (d.w) {
      this.custom = null;
      this.measure = null;
      this.pick.w = Number(d.w);
      const lens = this.#at().filter((s) => s.w === this.pick.w).map((s) => s.l);
      if (!lens.includes(this.pick.l)) this.pick.l = lens.includes(Number(this.cfg.most)) ? Number(this.cfg.most) : Math.max(...lens);
      this.#remember();
    } else if (d.l) {
      this.custom = null;
      this.measure = null;
      this.pick.l = Number(d.l);
      this.#remember();
    } else if ('customSheet' in d) {
      this.#sheetOpen(false);
      this.custom = null;
      this.measure = { w: '', l: '' };
      this.render();
      const box = $('[data-measure]', this);
      if (box) box.scrollIntoView({ block: 'center', behavior: calm() ? 'auto' : 'smooth' });
      $('[data-mw]', this)?.focus({ preventScroll: true });
      return;
    } else if ('custom' in d) {
      this.measure = this.measure ? null : { w: this.custom?.w ?? '', l: this.custom?.l ?? '' };
    } else if (d.unit) {
      this.unit = d.unit;
      try {
        localStorage.setItem('sunday-unit', this.unit);
      } catch (err) {}
    } else if ('use' in d) {
      this.#useCustom();
      return;
    } else if ('add' in d || 'barAdd' in d) {
      this.#add(b);
      return;
    } else if ('chip' in d) {
      this.#sheetOpen(this.$sheet.hidden);
      return;
    } else if ('x' in d) {
      this.#sheetOpen(false);
      this.$chip.focus();
      return;
    } else return;
    const keep = b.closest('[data-sizes-sheet]') ? 'sheet' : null;
    this.render();
    if ('custom' in d && this.measure) $('[data-mw]', this)?.focus();
    if (keep && d.w) $(`[data-sizes-sheet] [data-w="${d.w}"]`, this)?.focus();
    if (keep && d.l) $(`[data-sizes-sheet] [data-l="${d.l}"]`, this)?.focus();
  }

  #input(e) {
    if (!this.measure || !e.target.matches('[data-mw],[data-ml]')) return;
    const raw = e.target.value.replace(/[^\d.]/g, '');
    const n = raw === '' ? '' : Number(raw);
    const inches = n === '' ? '' : this.unit === 'cm' ? Math.round(n / CM) : Math.round(n);
    if (e.target.matches('[data-mw]')) this.measure.w = inches;
    else this.measure.l = inches;
    const tmp = document.createElement('div');
    tmp.innerHTML = this.#measureHtml();
    $('[data-cutsvg]', this.$measure).innerHTML = $('[data-cutsvg]', tmp).innerHTML;
    $('[data-cutres]', this.$measure).innerHTML = $('[data-cutres]', tmp).innerHTML;
    $('[data-use]', this.$measure).replaceWith($('[data-use]', tmp));
  }

  #useCustom() {
    const { w, l, from } = this.#measured();
    if (!from) return;
    const std = this.sizes.find((s) => s.w === w && s.l === l && (this.pick.h == null || s.h === this.pick.h));
    this.measure = null;
    if (std) {
      this.custom = null;
      this.pick = { h: std.h, w, l };
    } else this.custom = { w, l };
    this.#remember();
    this.render();
  }

  async #add(btn) {
    const v = this.chosen;
    if (!v || this.busy) return;
    this.busy = true;
    this.$err.hidden = true;
    this.#sheetOpen(false);
    const tp = this.pair && this.pair.on ? this.#pairNow() : null;
    const item = { btn, name: this.cfg.name, what: this.#what(), price: money(v.price + (tp ? tp.price : 0)), image: this.cfg.image, tint: this.cfg.tint, qty: tp ? 2 : 1 };
    document.dispatchEvent(new CustomEvent('sunday:adding', { detail: item }));
    try {
      const res = await cart.addFromProductPage({ variantId: v.id, productId: this.cfg.productId, size: { w: v.w, l: v.l }, custom: this.custom, also: tp ? [tp.id] : [] });
      const line = res.cart.items.find((x) => x.variant_id === v.id && (x.properties || {})._bed === res.bed) || res.cart.items[0];
      document.dispatchEvent(
        new CustomEvent('sunday:added', {
          detail: { ...item, cart: res.cart, line, replaceable: res.replaceable, protector: this.#protector(v, res.cart, res.bed) },
        })
      );
    } catch (err) {
      this.$err.textContent = err.message || '';
      this.$err.hidden = false;
      document.dispatchEvent(new CustomEvent('sunday:add-failed', { detail: { btn, message: err.message || '' } }));
    } finally {
      this.busy = false;
    }
  }

  #protector(v, c, bed) {
    const p = this.cfg.protector;
    if (!p) return null;
    const s = parseAll(p.variants).find((x) => x.w === v.w && x.l === v.l && x.available);
    if (!s) return null;
    if (c.items.some((x) => x.product_id === p.productId && (x.properties || {})._bed === bed)) return null;
    return { id: s.id, price: money(s.price), bed };
  }

  /* ---------- bubbles ---------- */
  #bubbles() {
    let pop = null;
    const close = () => {
      if (!pop) return;
      pop.remove();
      pop = null;
      $$('[data-tip][aria-expanded="true"]', this).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    };
    const open = (b) => {
      close();
      b.setAttribute('aria-expanded', 'true');
      pop = document.createElement('div');
      pop.className = 'sd-mh-pop';
      pop.setAttribute('role', 'tooltip');
      pop.textContent = b.dataset.tip;
      document.body.appendChild(pop);
      const r = b.getBoundingClientRect();
      const w = Math.min(260, innerWidth - 32);
      pop.style.width = w + 'px';
      const left = Math.max(16, Math.min(r.left + r.width / 2 - w / 2, innerWidth - w - 16));
      pop.style.left = left + 'px';
      pop.style.top = r.bottom + 6 + 'px';
    };
    document.addEventListener(
      'click',
      (e) => {
        const b = e.target.closest && e.target.closest('[data-tip]');
        if (b && this.contains(b)) {
          e.preventDefault();
          if (b.getAttribute('aria-expanded') === 'true') close();
          else open(b);
          return;
        }
        if (!(e.target.closest && e.target.closest('.sd-mh-pop'))) close();
      },
      true
    );
    document.addEventListener('keydown', (e) => e.key === 'Escape' && close());
    document.addEventListener('scroll', close, true);
  }

  /* ---------- gallery: render or a clip on the stage ---------- */
  #gallery() {
    const stage = $('[data-stage]', this);
    const img = $('img', stage);
    const clear = () => {
      $('video', stage)?.remove();
      $('[data-photo]', stage)?.remove();
      stage.removeAttribute('data-bare');
      stage.removeAttribute('data-tint');
      stage.removeAttribute('data-spin');
      if (img) img.hidden = false;
    };
    const ths = $$('[data-th]', this);
    // A sideways swipe on the stage steps to the next or previous picture.
    let x0 = null;
    let y0 = null;
    stage.addEventListener('pointerdown', (e) => {
      x0 = e.clientX;
      y0 = e.clientY;
    }, { passive: true });
    stage.addEventListener('pointerup', (e) => {
      if (x0 == null) return;
      const dx = e.clientX - x0;
      const dy = e.clientY - y0;
      x0 = null;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      const i = ths.findIndex((b) => b.getAttribute('aria-pressed') === 'true');
      const n = ths[(i + (dx < 0 ? 1 : -1) + ths.length) % ths.length];
      if (n) n.click();
    }, { passive: true });
    stage.style.touchAction = 'pan-y';
    ths.forEach((b) =>
      b.addEventListener('click', () => {
        ths.forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
        clear();
        const kind = b.dataset.th;
        if (kind === 'img' && b.dataset.tint != null) stage.setAttribute('data-tint', '');
        if (kind === 'photo') {
          const p = document.createElement('img');
          Object.assign(p, { src: b.dataset.full, alt: b.dataset.alt || '', decoding: 'async' });
          if (b.dataset.iw) {
            p.width = Number(b.dataset.iw);
            p.height = Number(b.dataset.ih);
          }
          if (b.dataset.pos) p.style.objectPosition = b.dataset.pos;
          if (b.dataset.bare != null) stage.setAttribute('data-bare', '');
          if (b.dataset.tint != null) stage.setAttribute('data-tint', '');
          p.dataset.photo = '';
          p.className = 'sd-mh-photo';
          if (img) img.hidden = true;
          stage.appendChild(p);
        } else if (kind === 'video' || kind === 'film') {
          const film = kind === 'film';
          const v = document.createElement('video');
          Object.assign(v, { muted: !film, loop: !film, playsInline: true, poster: b.dataset.poster || '', src: b.dataset.src, preload: 'metadata' });
          if (!film) v.setAttribute('muted', '');
          v.setAttribute('playsinline', '');
          v.setAttribute('aria-label', b.dataset.alt || b.getAttribute('aria-label') || '');
          if (b.dataset.pos) v.style.objectPosition = b.dataset.pos;
          if (b.dataset.bare != null) stage.setAttribute('data-bare', '');
          if (b.dataset.spin != null) stage.setAttribute('data-spin', '');
          if (film) {
            v.controls = true;
            v.className = 'sd-mh-film';
          }
          if (img) img.hidden = true;
          stage.appendChild(v);
          v.play().catch(() => {}); /* AR, 24 Oct: clips play with reduced motion too */
        }
      })
    );
  }

  /* ---------- the bar ---------- */
  #barWatch() {
    if (!this.$bar) return;
    document.body.classList.add('sd-mh-hasbar');
    const head = document.querySelector('[data-sunday-header]');
    const setTop = () => this.style.setProperty('--sd-mh-top', (head ? head.offsetHeight : 0) + 'px');
    setTop();
    addEventListener('resize', setTop);
    const on = (show) => {
      this.$bar.classList.toggle('sd-on', show);
      this.$bar.setAttribute('aria-hidden', String(!show));
      $$('button', this.$bar).forEach((b) => (b.tabIndex = show ? 0 : -1));
      document.body.classList.toggle('sd-mh-barshow', show);
      if (!show) this.#sheetOpen(false);
    };
    // 23 Oct (AR go), as live v2.4: the bar shows from load and steps away only while the in-page Add is at least
    // half in view; it also steps away at the talk card.
    const help = document.querySelector('#help');
    let tick = 0;
    const check = () => {
      tick = 0;
      const r = this.$add.getBoundingClientRect();
      const seen = r.height ? Math.max(0, Math.min(r.bottom, innerHeight) - Math.max(r.top, 0)) / r.height : 0;
      const before = !help || help.getBoundingClientRect().top > innerHeight - 40;
      const show = seen < 0.5 && before;
      if (show !== this.$bar.classList.contains('sd-on')) {
        on(show);
        this.dispatchEvent(new CustomEvent('sunday:bar', { bubbles: true }));
      }
    };
    const ask = () => {
      if (!tick) tick = requestAnimationFrame(check);
    };
    addEventListener('scroll', ask, { passive: true });
    document.addEventListener('scroll', ask, { passive: true, capture: true });
    addEventListener('resize', ask);
    check();
    document.addEventListener('click', (e) => {
      if (this.$sheet && !this.$sheet.hidden && e.target.isConnected && (!e.target.closest('[data-bar]') || e.target === this.$sheet)) this.#sheetOpen(false);
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.$sheet && !this.$sheet.hidden) {
        this.#sheetOpen(false);
        this.$chip.focus();
      }
    });
  }
  #sheetOpen(open) {
    if (!this.$sheet) return;
    this.$sheet.hidden = !open;
    this.$chip.setAttribute('aria-expanded', String(open));
  }
}

if (!customElements.get('sunday-m-hero')) customElements.define('sunday-m-hero', SundayMHero);
